// 1:1 port from age-of-mcp v3 (src/client/models/v3/sawmill.ts) — the
// flattened part literals rebuilt from the original helpers + loops.
//
// v3 sawmill — AUTHORED FOR THE 45° CAMERA. An open timber WORK YARD whose
// icon is an OVERSIZED upright circular saw blade over an open bench, with a
// log feeding through it; a log pile waits in the east yard and the cut
// planks store under a small leaf-green (ROOF_WOODIND) half-open shed in the
// back-west corner — the shed shelters storage only, never the work spots,
// so the sawyer's bench-work animation reads fully from above.

import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, ROOF_WOODIND, STEEL, TIMBER, WOOD, WOOD_DK } = styleV3;

const BASE = 0.12;           // foundation slab top
const SAW_X = 0.08;          // the blade sits here along the bench

// The open saw bench across the middle of the yard.
function bench(): Part {
  return box(0.78, 0.16, 0.34, WOOD, [0, BASE + 0.18, 0.04]);
}

// The oversized circular saw blade — the building's icon from above.
function blade(): Part[] {
  return [
    cyl(0.27, 0.27, 0.05, 12, STEEL, [SAW_X, BASE + 0.5, 0.04], [Math.PI / 2, 0, 0]),
    cyl(0.06, 0.06, 0.09, 6, TIMBER, [SAW_X, BASE + 0.5, 0.04], [Math.PI / 2, 0, 0]),
  ];
}

// The log being fed through the blade, lying along the bench.
function feedLog(): Part {
  return cyl(0.07, 0.07, 0.6, 7, WOOD_DK, [-0.14, BASE + 0.33, 0.04], [0, 0, Math.PI / 2]);
}

// Four posts of the back-west storage shed (the roof comes with stage 3).
function shedPosts(): Part[] {
  const cx = -0.3, cz = -0.3;
  const out: Part[] = [];
  for (const [x, z] of [[-0.16, -0.16], [0.16, -0.16], [-0.16, 0.16], [0.16, 0.16]] as const) {
    out.push(cyl(0.028, 0.028, 0.5, 5, TIMBER, [cx + x, BASE + 0.25, cz + z]));
  }
  return out;
}

// The green shed roof over the plank store — the v3 wood-industry code.
function shedRoof(): Part {
  return box(0.46, 0.05, 0.46, ROOF_WOODIND, [-0.3, BASE + 0.53, -0.3], [0.16, 0, 0]);
}

// Cut planks stacked under the shed.
function plankStack(): Part[] {
  return [
    box(0.3, 0.05, 0.26, WOOD,    [-0.3, BASE + 0.03, -0.3]),
    box(0.28, 0.05, 0.22, WOOD_DK, [-0.3, BASE + 0.08, -0.3], [0, 0.12, 0]),
  ];
}

// A log pile waiting in the east yard.
function logPile(): Part[] {
  const r = 0.07;
  return [
    cyl(r, r, 0.5, 6, WOOD_DK, [0.3,  BASE + r, 0],       [Math.PI / 2, 0, 0]),
    cyl(r, r, 0.5, 6, WOOD,    [0.42, BASE + r, 0],       [Math.PI / 2, 0, 0]),
    cyl(r, r, 0.5, 6, WOOD_DK, [0.36, BASE + r * 2.7, 0], [Math.PI / 2, 0, 0]),
  ];
}

// A sawmill: an open work yard around an oversized circular saw, planks under
// a green half-shed, logs in the yard. Grows through the four build stages.
function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.9, 0.9)];
  if (s === 1) return [slab(0.9, 0.9), bench(), ...plankStack(), ...poles(0.84, 0.84, 0.55)];
  if (s === 2) {
    return [
      slab(0.9, 0.9), bench(), ...blade(), feedLog(),
      ...shedPosts(), ...plankStack(), ...poles(0.84, 0.84, 0.9),
    ];
  }
  return [
    slab(0.9, 0.9), bench(), ...blade(), feedLog(),
    ...shedPosts(), shedRoof(), ...plankStack(), ...logPile(),
  ];
}

export const sawmillV3 = defineModel({
  id: "sawmill-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Sawmill (v3)",
  tags: ["building","sawmill","low-poly","age-of-mcp","v3"],
  summary: "Low-poly sawmill.",
  description: "A timber saw bench that saws raw wood into planks.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench-a", position: [0.08, 0.12, 0.45], facing: Math.PI },
    { id: "bench-b", position: [0.14, 0.12, -0.4], facing: 0 },
  ],
  interactions: [
    { id: "saw", label: "Sawing", station: "bench-a", variants: ["complete"], ...benchWork() },
  ],
});
